import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import 'react-datepicker/dist/react-datepicker.css';
import DatePicker from 'react-datepicker';
import ptBR from 'date-fns/locale/pt-BR';
import { withTranslation } from 'react-i18next';
import format from 'date-fns/format';
import { makeStyles } from '@material-ui/core/styles';
import { scrollToTop } from '../../utils/smartutils';

const DateSelection = (props) => {
  const { months, minDate, popperPlacement, meta, input, def } = props;

  const [selectionComplete, toggleSelectionComplete] = useState(false);
  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();
  const [firstClick, setFirstClick] = useState(true);

  const useStyles = makeStyles(() => ({
    field: {
      width: '100%',
      padding: '5px 10px',
      border: '1px solid #e0e1e2',
      fontSize: '12px',
      height: '32px',
      cursor: 'text !important',
      textAlign: 'inherit',
      '&:focus': {
        borderColor: '#5b346d',
      },
      background: '#FFF',
    },
  }));

  const classes = useStyles();

  const handleDateChange = (date) => {
    if (!selectionComplete && firstClick) {
      setStartDate(date);
      setEndDate(date);
      setFirstClick(!firstClick);

      return;
    }
    if (!selectionComplete && startDate && date > startDate) {
      setEndDate(date);
      toggleSelectionComplete(true);
      return;
    }
    if (!selectionComplete && !firstClick && date < startDate) {
      setStartDate(date);
      setEndDate(date);
      return;
    }
    if (selectionComplete && startDate && endDate) {
      setStartDate(date);
      setEndDate(date);
      toggleSelectionComplete(false);
    }
  };

  const sameDay = (d1, d2) =>
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate();

  const handleSelect = (date) => {
    if (
      !selectionComplete &&
      startDate &&
      !endDate &&
      sameDay(date, startDate)
    ) {
      handleDateChange(date);
    }
  };

  const MyContainer = ({ children }) => (
    <div className="date-selection">
      <div className="row">{children}</div>
      <div className="action-row p-2">
        <p className="d-flex justify-content-around align-items-baseline">
          {startDate ? `${format(startDate, 'dd/MM/yyyy')}` : null}
          {endDate ? ` - ${format(endDate, 'dd/MM/yyyy')}` : null}
        </p>
      </div>
    </div>
  );

  MyContainer.propTypes = {
    children: PropTypes.arrayOf(PropTypes.shape).isRequired,
  };

  useEffect(() => {
    const { initialDate, finalDate } = def;
    const timeElapsed = Date.now();
    setStartDate(
      new Date(initialDate ? new Date(initialDate) : new Date(timeElapsed))
    );
    setEndDate(
      new Date(finalDate ? new Date(finalDate) : new Date(timeElapsed))
    );
  }, []);

  useEffect(() => {
    input.onChange({ startDate, endDate });
  }, [startDate, endDate]);

  return (
    <div className="form__form-group-input-wrap form__form-group-input-wrap--error-above">
      <div className="date-field">
        <DatePicker
          onChange={handleDateChange}
          onSelect={handleSelect}
          locale={ptBR}
          shouldCloseOnSelect={false}
          popperPlacement={popperPlacement || 'top-end'}
          minDate={minDate}
          selectsEnd={Boolean(startDate)}
          startDate={startDate}
          endDate={endDate}
          monthsShown={months}
          calendarContainer={MyContainer}
          customInput={
            <button
              type="button"
              className={[classes.field, 'field-range-date']}
            >
              {startDate &&
                `${format(startDate, 'dd/MM/yyyy')}${
                  endDate ? `- ${format(endDate, 'dd/MM/yyyy')}` : ''
                }`}
            </button>
          }
        />
      </div>
      {meta.touched && meta.error && (
        <span className="form__form-group-error">
          {meta.error}
          {scrollToTop()}
        </span>
      )}
    </div>
  );
};

DateSelection.propTypes = {
  months: PropTypes.number,
  minDate: PropTypes.oneOfType(PropTypes.date),
  popperPlacement: PropTypes.string,
  meta: PropTypes.shape(PropTypes.shape),
  input: PropTypes.shape(PropTypes.shape).isRequired,
  def: PropTypes.bool,
};

DateSelection.defaultProps = {
  months: 1,
  minDate: null,
  popperPlacement: 'top-end',
  def: false,
  meta: {
    touched: false,
    error: '',
  },
};

export default withTranslation('common')(DateSelection);
